import { compact, uniq, zip } from 'lodash/fp';

import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { isTerminalStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import { NON_WEB_PREVIEW_COMPONENTS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import { getAllWorkflowActionSteps } from 'dashboard/apps/InquiryTemplateEditor/utils/workflow-steps';
import { InquiryStatus } from 'dashboard/models/inquiry';
import {
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
  isCombinedStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { AllWorkflowAction, WorkflowStepAction } from 'dashboard/models/workflow-version';
import { StepUi, UiStepType } from 'lib/interfaces/StepsConfig';
import {
  AssetSource,
  isUiParentComponent,
  isUiParentComponentConfig,
  isUiTransitionComponentConfig,
  TransitionConfigAttributes,
  UiComponent,
  UiComponentConfig,
  UiComponentConfigType,
} from 'lib/interfaces/ui-components';
import { assertNever } from 'lib/utils/types';

// The only statuses that can be reached via navigating through the flow
const VALID_FLE_STATUS_OPTIONS = [
  InquiryStatus.Completed,
  InquiryStatus.Failed,
  InquiryStatus.Created,
  // Omitting pending status for now until we figure out how we want to display/configure steps
  // with more than one status
];

/*
 *  Helper function to help us only render statuses that can be reached in the
 *  inquiry flow via user navigation
 */
export const getValidStepStatus = (statuses: InquiryStatus[] | undefined) => {
  if (!statuses) return null;
  // Retrieve the first valid statues from the list of statuses
  const validStepStatuses = statuses.filter((status) => VALID_FLE_STATUS_OPTIONS.includes(status));
  return validStepStatuses?.[0];
};

export const getStepConfigAndPath = (
  steps: InquiryTemplateVersionStep[],
  predicateFn: (step: InquiryTemplateVersionStep) => boolean,
): { stepConfig: InquiryTemplateVersionStep | null; stepConfigPath: string | null } => {
  const index = steps.findIndex(predicateFn);
  if (index !== -1) {
    return { stepConfig: steps[index], stepConfigPath: index.toString() };
  } else {
    return { stepConfig: null, stepConfigPath: null };
  }
};

export const isInitialStep = (step: InquiryTemplateVersionStep): boolean =>
  step?.config?.statuses?.some((status) => status?.initial);

export const backStepTargetOptions = ({
  steps,
  currentStep,
}: {
  steps: InquiryTemplateVersionStep[];
  currentStep: InquiryTemplateVersionStep | undefined;
}): InquiryTemplateVersionStep[] => {
  if (currentStep == null) {
    return [];
  }
  return steps.filter(
    (step) =>
      step.name !== currentStep.name && !isTerminalStep(step) && step.type !== UiStepType.Complete,
  );
};

/**
 * Returns first step found with initial status: true
 * @param steps list of ITV steps
 */
export const firstInitialStep = (
  steps: InquiryTemplateVersionStep[],
): InquiryTemplateVersionStep | undefined =>
  steps.find((s) => s.config.statuses.some((status) => status.initial));

/**
 * Extracts all to-step targets from a list of ITV steps
 * @param steps list of ITV steps
 * @returns {string[]} list of step names
 */
export const stepsToStepTargets = (steps: InquiryTemplateVersionStep[]): string[] =>
  uniq(compact(steps).flatMap(stepToStepTargets));

const stepToStepTargets = (step: InquiryTemplateVersionStep): string[] => {
  if (step?.type == null) return [];
  switch (step.type) {
    case UiStepType.Complete:
      return [];
    case UiStepType.Document:
    case UiStepType.GovernmentId:
    case UiStepType.Integration:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
    case UiStepType.VerifyWithPersona:
      return compact([step.config['to-step']]);
    case UiStepType.Ui:
      return uiStepToStepTargets(step);
    case UiStepType.Loop:
      return step.config.steps.map((step) => stepToStepTargets(step)).flat();
    default:
      assertNever(step);
  }
};

/**
 * Extracts all workflow-version-id targets from a list of ITV steps
 * @param steps list of ITV steps
 * @returns {string[]} list of workflow-version-ids
 */
export const stepsWorkflowVersionTargets = (steps: InquiryTemplateVersionStep[]): string[] =>
  uniq(compact(steps).flatMap(stepWorkflowVersionTargets));

export const stepWorkflowVersionTargets = (step: InquiryTemplateVersionStep): string[] => {
  if (step?.type == null) return [];
  switch (step.type) {
    case UiStepType.Complete:
      return [];
    case UiStepType.Document:
    case UiStepType.GovernmentId:
    case UiStepType.Integration:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
    case UiStepType.VerifyWithPersona:
      return compact([step.config['workflow-version-id']]);
    case UiStepType.Ui:
      return uiStepWorkflowVersionTargets(step);
    case UiStepType.Loop:
      return step.config.steps.map((step) => stepWorkflowVersionTargets(step)).flat();
    default:
      assertNever(step);
  }
};

/**
 * Extracts all workflow-on-error targets from a list of ITV steps
 * @param steps list of ITV steps
 * @returns {string[]} list of step names
 */
export const stepsWorkflowErrorTargets = (steps: InquiryTemplateVersionStep[]): string[] =>
  uniq(compact(steps).flatMap(stepWorkflowErrorTargets));

const stepWorkflowErrorTargets = (step: InquiryTemplateVersionStep): string[] => {
  if (step?.type == null) return [];
  switch (step.type) {
    case UiStepType.Complete:
    case UiStepType.Loop:
      return [];
    case UiStepType.Document:
    case UiStepType.GovernmentId:
    case UiStepType.Integration:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
    case UiStepType.VerifyWithPersona:
      return compact([step.config['workflow-on-error-step-name']]);
    case UiStepType.Ui:
      return uiStepWorkflowErrorTargets(step);
    default:
      assertNever(step);
  }
};

export const stepActionTargets = ({
  step,
  workflowVersionMap,
}: {
  step: InquiryTemplateVersionStep;
  workflowVersionMap: WorkflowVersionMap;
}): AllWorkflowAction[] => {
  return stepsWorkflowVersionTargets([step]).flatMap((wfvId) => {
    const workflowVersion = workflowVersionMap?.[wfvId];
    if (workflowVersion == null) {
      return [];
    }

    return getAllWorkflowActionSteps(workflowVersion.attributes['steps-config'])
      .flatMap((action: WorkflowStepAction) => {
        return action.action as AllWorkflowAction;
      })
      .filter(Boolean);
  });
};

/**
 * Extracts all verification-template-version-id references from a list of ITV steps
 * @param steps list of ITV steps
 * @returns {string[]} list of verification-template-version-ids
 */
export const stepsVtmplvIds = (steps: InquiryTemplateVersionStep[]): string[] =>
  uniq(
    steps
      .filter(isCombinedStepConfig)
      .flatMap((combinedStep) => combinedStep.config['verification-template-version-id'])
      .filter(Boolean),
  );

/**
 * Given a UI step client data, returns a list of un-nested components.
 */
export const uiStepAllComponents = (step: StepUi | undefined): UiComponent[] =>
  step != null ? uiStepComponentsRecursive(step.config.components) : [];

export const uiStepComponentsRecursive = (components: UiComponent[]): UiComponent[] =>
  components.flatMap((component) =>
    isUiParentComponent(component)
      ? [component, ...uiStepComponentsRecursive(component.attributes.children)]
      : [component],
  );

/**
 * Given a UI step client data and steps config, returns a list of un-nested component client data
 * and a list of un-nested component config, with "virtual" client data not mapping to config excluded,
 * such that the two lists are equal in length.
 *
 * Because "virtual" children are not configurable, we want to ignore those children for this
 * purpose of mapping config to client data.
 */
export const uiStepAllNonVirtualComponentsAndConfig = ({
  step,
  stepConfig,
}: {
  step: StepUi;
  stepConfig: InquiryTemplateVersionStepUi;
}): { stepUiComponents: UiComponent[]; stepUiComponentConfigs: UiComponentConfig[] } | undefined =>
  uiStepAllNonVirtualComponentsAndConfigRecursive({
    components: step.config.components,
    uiComponentConfigs: stepConfig.config.components,
  });

const uiStepAllNonVirtualComponentsAndConfigRecursive = ({
  components,
  uiComponentConfigs,
}: {
  components: UiComponent[];
  uiComponentConfigs: UiComponentConfig[];
}): { stepUiComponents: UiComponent[]; stepUiComponentConfigs: UiComponentConfig[] } => {
  // TODO (eric): remove once theme set rollout is complete - this is temporarily needed
  // as theme_set source image components will serialize nil client data if no theme set is set
  // on the template or org. this should no longer be the case once theme set rollout is complete.
  const allComponents = uiStepComponentsRecursive(components);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return zip(
    components,
    uiComponentConfigs.filter((componentConfig) => {
      if (NON_WEB_PREVIEW_COMPONENTS.includes(componentConfig.type)) return false;
      if (
        componentConfig.type === UiComponentConfigType.ImageHosted &&
        componentConfig.config.source === AssetSource.ThemeSet
      ) {
        return allComponents.find((c) => c.name === componentConfig.name) != null;
      }
      return true;
    }),
  ).reduce(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (total, [component, componentConfig]) => {
      // The former may be true while the latter false. This is because some components,
      // like stepper, may have config that is not a parent (e.g. stepper), but be represented in client data
      // by a parent component (e.g. horizontal stack in the case of stepper). Because a stepper's "virtual"
      // children are not configurable, we want to ignore those children for this purpose of mapping config to
      // client data
      if (isUiParentComponent(component) && isUiParentComponentConfig(componentConfig)) {
        const {
          stepUiComponents: nestedStepUiComponents,
          stepUiComponentConfigs: nestedStepUiComponentConfigs,
        } = uiStepAllNonVirtualComponentsAndConfigRecursive({
          components: component.attributes.children,
          uiComponentConfigs: componentConfig.config.children,
        });
        return {
          stepUiComponents: [...total.stepUiComponents, component, ...nestedStepUiComponents],
          stepUiComponentConfigs: [
            ...total.stepUiComponentConfigs,
            componentConfig,
            ...nestedStepUiComponentConfigs,
          ],
        };
      } else {
        return {
          stepUiComponents: [...total.stepUiComponents, component],
          stepUiComponentConfigs: [...total.stepUiComponentConfigs, componentConfig],
        };
      }
    },
    {
      stepUiComponents: [],
      stepUiComponentConfigs: [],
    },
  );
};

/**
 * Given a UI step server config, returns a list of un-nested component configs.
 */
export const uiStepAllComponentConfigs = (
  step: InquiryTemplateVersionStepUi | undefined,
): UiComponentConfig[] =>
  step != null ? uiStepComponentConfigsRecursive(step.config.components) : [];

export const uiStepComponentConfigsRecursive = (
  componentConfigs: UiComponentConfig[],
): UiComponentConfig[] =>
  componentConfigs.flatMap((componentConfig) =>
    isUiParentComponentConfig(componentConfig)
      ? [componentConfig, ...uiStepComponentConfigsRecursive(componentConfig.config.children)]
      : [componentConfig],
  );

/**
 * Extracts all to-step targets from a UI step's transition components
 * @param step UI step
 * @returns {string[]} list of step names
 */
const uiStepToStepTargets = (step: InquiryTemplateVersionStepUi): string[] =>
  uiStepTransitionConfigAttributes(step, 'to-step');

/**
 * Extracts all workflow-version-id targets from a UI step's transition components
 * @param step UI step
 * @returns {string[]} list of workflow-version-ids
 */
export const uiStepWorkflowVersionTargets = (step: InquiryTemplateVersionStepUi): string[] =>
  uiStepTransitionConfigAttributes(step, 'workflow-version-id');

/**
 * Extracts all workflow-on-error targets from a UI step's transition components
 * @param step UI step
 * @returns {string[]} list of step names
 */
const uiStepWorkflowErrorTargets = (step: InquiryTemplateVersionStepUi): string[] =>
  uiStepTransitionConfigAttributes(step, 'workflow-on-error-step-name');

/**
 * Helper for extracting transition config values from a ui step's components
 * @param step UI step
 * @param transitionConfigKey
 * @returns {string[]} list of step names
 */
const uiStepTransitionConfigAttributes = (
  step: InquiryTemplateVersionStepUi,
  transitionConfigKey: keyof TransitionConfigAttributes,
): string[] =>
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  uiStepAllComponentConfigs(step)
    .filter(isUiTransitionComponentConfig)
    .map((component) => component.config[transitionConfigKey])
    .filter(Boolean);
